import { isSupportedItemType } from "./../utils/item-type-checker";
import * as AST from "./sfml-ast";

function selectXMLChildren(xmlElement: Element, nodeName: string) {
  const foundNodes = [];

  const xmlChildren = xmlElement.children;
  for (let i = 0, n = xmlChildren.length; i < n; ++i) {
    const xmlNode = xmlChildren[i];
    if (xmlNode.nodeName === nodeName) foundNodes.push(xmlNode);
  }
  return foundNodes;
}

function parseScrollView(xmlElement: Element, base: AST.UIElementBase): AST.ScrollViewElement | null {
  const kind = "scrollview";

  const orientation = xmlElement.getAttribute("orientation") || "vertical";
  if (orientation !== "vertical" && orientation !== "horizontal") return null;

  const xmlContent = xmlElement.children[0];
  if (!xmlContent) return null;

  const content = parseUIElement(xmlContent);
  if (!content) return null;

  return { ...base, kind, orientation, content };
}

function parseLinearLayout(xmlElement: Element, base: AST.UIElementBase): AST.LinearLayoutElement | null {
  const kind = "linear-layout";

  const orientation = xmlElement.getAttribute("orientation") || "vertical";
  if (orientation !== "vertical" && orientation !== "horizontal") return null;

  const children: AST.UIElement[] = [];
  const xmlChildren = xmlElement.children;
  for (let i = 0, n = xmlChildren.length; i < n; ++i) {
    const xmlContent = xmlChildren[i];
    const content = parseUIElement(xmlContent);
    if (content) children.push(content);
  }

  return { ...base, kind, orientation, content: children };
}

function parseCollapsibleLayout(xmlElement: Element, base: AST.UIElementBase): AST.CollapsibleLayoutElement | null {
  const kind = "collapsible-layout";

  const orientation = xmlElement.getAttribute("orientation") || "vertical";
  if (orientation !== "vertical" && orientation !== "horizontal") return null;

  const title = xmlElement.getAttribute("title");
  if (title === null) return null;

  const exposedTitle = xmlElement.getAttribute("exposed-title") || title;

  const collapseType = parseCollapseType(xmlElement, "collapse-type") || "expose";

  const icon = xmlElement.getAttribute("icon") || null;
  const iconUrl = xmlElement.getAttribute("icon-url") || null;

  const children: AST.UIElement[] = [];
  const xmlChildren = xmlElement.children;
  for (let i = 0, n = xmlChildren.length; i < n; ++i) {
    const xmlContent = xmlChildren[i];
    const content = parseUIElement(xmlContent);
    if (content) children.push(content);
  }

  return {
    ...base,
    kind,
    orientation,
    title,
    exposedTitle,
    icon,
    iconUrl,
    collapseType,
    content: children,
  };
}

function parseFlexbox(xmlElement: Element, base: AST.UIElementBase): AST.FlexboxElement | null {
  const kind = "flexbox";

  const orientation = xmlElement.getAttribute("orientation") || "vertical";
  if (orientation !== "vertical" && orientation !== "horizontal") return null;

  const children: AST.UIElement[] = [];
  const xmlChildren = xmlElement.children;
  for (let i = 0, n = xmlChildren.length; i < n; ++i) {
    const xmlContent = xmlChildren[i];
    const content = parseUIElement(xmlContent);
    if (content) children.push(content);
  }

  return { ...base, kind, orientation, content: children };
}

function parseColor(xmlElement: Element, attribute: string): AST.Color | null {
  const colorString = xmlElement.getAttribute(attribute);
  if (!colorString || colorString.length !== 7 || colorString[0] !== "#") return null;

  function isHexCharacter(char: string) {
    return (
      char[0] === "0" ||
      char[0] === "1" ||
      char[0] === "2" ||
      char[0] === "3" ||
      char[0] === "4" ||
      char[0] === "5" ||
      char[0] === "6" ||
      char[0] === "7" ||
      char[0] === "8" ||
      char[0] === "9" ||
      char[0] === "a" ||
      char[0] === "b" ||
      char[0] === "c" ||
      char[0] === "d" ||
      char[0] === "e" ||
      char[0] === "f"
    );
  }

  const lowerCaseColorString = colorString.toLowerCase();
  if (
    !isHexCharacter(lowerCaseColorString[1]) ||
    !isHexCharacter(lowerCaseColorString[2]) ||
    !isHexCharacter(lowerCaseColorString[3]) ||
    !isHexCharacter(lowerCaseColorString[4]) ||
    !isHexCharacter(lowerCaseColorString[5]) ||
    !isHexCharacter(lowerCaseColorString[6])
  )
    return null;

  const r = parseInt(lowerCaseColorString.substr(1, 2), 16) / 255;
  const g = parseInt(lowerCaseColorString.substr(3, 2), 16) / 255;
  const b = parseInt(lowerCaseColorString.substr(5, 2), 16) / 255;

  return { r, g, b };
}

function parseRect(xmlElement: Element, attribute: string): AST.Rect | null {
  const parts = parseNumberList(xmlElement, attribute);
  if (!parts || parts.length !== 4) return null;

  return {
    left: parts[0],
    top: -parts[1],
    width: parts[2] - parts[0],
    height: parts[1] - parts[3],
  };
}

function parseMargin(xmlElement: Element, attribute: string): AST.Margin | null {
  const parts = parseNumberList(xmlElement, attribute);
  if (!parts || parts.length !== 4) return null;

  return {
    left: parts[0],
    top: parts[1],
    right: parts[2],
    bottom: parts[3],
  };
}

function parseNumberList(xmlElement: Element, attribute: string): number[] | null {
  const value = xmlElement.getAttribute(attribute);
  if (!value) return null;

  const parts = value.split(" ").filter((x) => x.length !== 0);
  const numbers: number[] = [];
  for (let part of parts) {
    const number = Number(part);
    if (isNaN(number)) return null;

    numbers.push(number);
  }

  return numbers;
}

function parseNumber(xmlElement: Element, attribute: string): number | null {
  const value = xmlElement.getAttribute(attribute);
  if (!value) return null;

  const number = Number(value);
  if (isNaN(number)) return null;

  return number;
}

function parseMeasureSpec(xmlElement: Element, attribute: string): AST.MeasureSpec | null {
  const value = xmlElement.getAttribute(attribute);
  if (!value) return null;

  if (value === "match-parent" || value === "wrap-content" || value === "preserve-aspect") return value;

  const number = parseInt(value, 10);
  if (isNaN(number)) return null;

  return number;
}

function parseCollapseType(xmlElement: Element, attribute: string): AST.CollapseType | null {
  const value = xmlElement.getAttribute(attribute);

  if (value === "expose" || value === "replace") return value;
  else return null;
}

function parsePayload(xmlElement: Element) {
  const payload = new Map<string, string>();

  for (let i = 0, n = xmlElement.attributes.length; i < n; ++i) {
    const attr = xmlElement.attributes[i];
    if (attr.name.startsWith(AST.PAYLOAD_PREFIX)) {
      payload.set(attr.name.slice(AST.PAYLOAD_PREFIX.length), attr.value);
    }
  }

  return payload;
}

function parseFontFamily(xmlElement: Element, attribute: string): string | null {
  const value = xmlElement.getAttribute(attribute);

  if (!value) return null;

  return value;
}

function parseFontWeight(xmlElement: Element, attribute: string): AST.FontWeight | null {
  const value = xmlElement.getAttribute(attribute);

  if (value === "bold" || value === "normal") return value;

  return null;
}

function parseArea(xmlElement: Element): AST.AreaElement | null {
  const href = xmlElement.getAttribute("href");
  if (!href) return null;

  const label = xmlElement.getAttribute("label");
  if (!label) return null;

  const rect = parseRect(xmlElement, "rect");
  if (!rect) return null;

  const itemType = (xmlElement.getAttribute("item-type") || "flyer-item") as AST.ItemType;
  if (!isSupportedItemType(itemType)) {
    return null;
  }

  const itemId = xmlElement.getAttribute("source-id");
  if (!itemId) return null;

  const targetAnchorId = xmlElement.getAttribute("target-anchor-id");

  const payload = parsePayload(xmlElement);

  // NOTE: Just realized that "href" is a required attribute in the area tag.
  //       Ideally we should move onto using the action meta-element to
  //       determine the action, e.g. clip, link, etc
  // TODO: Refactor once this is addressed in the SFML spec
  let action = null;
  for (let i = 0; i < xmlElement.children.length; i++) {
    if (xmlElement.children[i].nodeName === "action") {
      action = parseAction(xmlElement.children[i]);
      break;
    }
  }

  return { href, label, rect, itemType, itemId, targetAnchorId, payload, action };
}

function parseAction(xmlElement: Element): AST.ActionElement | null {
  const type = xmlElement.getAttribute("type");
  switch (type) {
    case AST.Action.LINK:
      const ttmUrl = xmlElement.getAttribute("ttm-url");
      return { type, ttmUrl };
    default:
      return null;
  }
}

function parseBadgePosition(xmlElement: Element, coordinate: "x" | "y"): number | null {
  const position = xmlElement.getAttribute(coordinate);
  switch (position) {
    case "start": {
      return 0;
    }
    case "center": {
      return 0.5;
    }
    case "end": {
      return 1;
    }
    default: {
      return null;
    }
  }
}

function parseBadge(xmlElement: Element): AST.BadgeElement | null {
  const kind = "badge";

  const vpos = parseNumber(xmlElement, "v-pos");
  if (vpos == null) return null;

  const hpos = parseNumber(xmlElement, "h-pos");
  if (hpos == null) return null;

  const x = parseBadgePosition(xmlElement, "x");
  if (x === null) return null;

  const y = parseBadgePosition(xmlElement, "y");
  if (y === null) return null;

  const width = parseNumber(xmlElement, "width");
  if (!width) return null;

  const height = parseNumber(xmlElement, "height");
  if (!height) return null;

  const url = xmlElement.getAttribute("url");
  if (!url) return null;
  const attachedProperties = parseAttachedProperties(xmlElement);

  return { kind, hpos, vpos, x, y, width, height, url, attachedProperties };
}

function parseItemAtom(xmlElement: Element, base: AST.UIElementBase): AST.ItemAtomElement | null {
  const kind = "item-atom";
  const itemId = xmlElement.getAttribute("source-id");
  if (!itemId) return null;

  const itemType = (xmlElement.getAttribute("item-type") || "flyer-item") as AST.ItemType;
  if (!isSupportedItemType(itemType)) {
    return null;
  }

  const flexGrow = parseNumber(xmlElement, "flex-grow") || 1;
  const imagePosition = (xmlElement.getAttribute("image-position") || "image-top") as AST.ImagePosition;
  const imageUrl = xmlElement.getAttribute("image-url") || "";
  const imageWidth = parseNumber(xmlElement, "image-width") || 0;
  const imageHeight = parseNumber(xmlElement, "image-height") || 0;

  const orientation = xmlElement.getAttribute("orientation");
  if (orientation) {
    console.warn("Orientation is a deprecated attribute in item-atom element. Use image-position instead.");
  }

  const children = xmlElement.children;
  const badges: AST.BadgeElement[] = [];
  const texts: AST.TextElement[] = [];
  for (let i = 0, n = children.length; i < n; ++i) {
    const child = children[i];
    if (child.nodeName === "badge") {
      const source = parseBadge(child);
      if (source) badges.push(source);
    } else if (child.nodeName === "text") {
      const text = parseText(child, base);
      if (text) texts.push(text);
    }
  }

  const targetAnchorId = xmlElement.getAttribute("target-anchor-id");

  const payload = parsePayload(xmlElement);

  return {
    ...base,
    kind,
    itemType,
    itemId,
    targetAnchorId,
    badges,
    texts,
    payload,
    imagePosition,
    flexGrow,
    imageUrl,
    imageWidth,
    imageHeight,
  };
}

function parseUrlSource(xmlElement: Element): AST.UrlSourceElement | null {
  const kind = "source";
  const format = "url";

  const width = parseNumber(xmlElement, "width");
  if (width === null) return null;

  const height = parseNumber(xmlElement, "height");
  if (height === null) return null;

  const url = xmlElement.getAttribute("url");
  if (!url) return null;

  const areas: AST.AreaElement[] = [];
  const children = xmlElement.children;
  for (let i = 0, n = children.length; i < n; ++i) {
    const child = children[i];
    if (child.nodeName === "area") {
      const area = parseArea(child);
      if (area) areas.push(area);
    }
  }

  return { kind, format, width, height, url, areas };
}

function parseFlyerSource(xmlElement: Element): AST.FlyerSourceElement | null {
  const kind = "source";
  const format = "tiled";

  const width = parseNumber(xmlElement, "width");
  if (width === null) return null;

  const height = parseNumber(xmlElement, "height");
  if (height === null) return null;

  const path = xmlElement.getAttribute("path");
  if (!path) return null;

  const resolutions = parseNumberList(xmlElement, "resolutions");
  if (!resolutions || resolutions.length === 0) return null;

  const rect = parseRect(xmlElement, "rect");
  if (!rect) return null;

  const areas: AST.AreaElement[] = [];
  const children = xmlElement.children;
  for (let i = 0, n = children.length; i < n; ++i) {
    const child = children[i];
    if (child.nodeName === "area") {
      const area = parseArea(child);
      if (area) areas.push(area);
    }
  }

  return { kind, format, width, height, rect, areas, path, resolutions };
}

function parseText(xmlElement: Element, base: AST.UIElementBase): AST.TextElement | null {
  const kind = "text";

  const value = xmlElement.getAttribute("value");
  if (value === null) return null;

  const maxLines = parseNumber(xmlElement, "max-lines") || 1;
  const styleId = xmlElement.getAttribute("style-id");

  return { ...base, kind, maxLines, value, styleId };
}

function parseImage(xmlElement: Element, base: AST.UIElementBase): AST.ImageElement | null {
  const kind = "image";
  const flexGrow = parseNumber(xmlElement, "flex-grow") || 1;

  // TODO: Seeing a lot of replicate of the same logic. We should DRY them
  const sources: AST.SourceElement[] = [];
  const children = xmlElement.children;
  const badges: AST.BadgeElement[] = [];
  for (let i = 0, n = children.length; i < n; ++i) {
    const child = children[i];

    if (child.nodeName === "url-source") {
      const source = parseUrlSource(child);
      if (source) sources.push(source);
    } else if (child.nodeName === "flyer-source") {
      const source = parseFlyerSource(child);
      if (source) sources.push(source);
    } else if (child.nodeName === "badge") {
      const source = parseBadge(child);
      if (source) badges.push(source);
    }
  }

  return { ...base, kind, sources, flexGrow, badges };
}

function parseAttachedProperties(xmlElement: Element) {
  const attachedProperties: { [key: string]: string } = {};
  for (let i = 0; i < xmlElement.attributes.length; i++) {
    const attribute = xmlElement.attributes[i];
    if (attribute.nodeValue === null) continue;

    attachedProperties[attribute.nodeName] = attribute.nodeValue;
  }

  return attachedProperties;
}

function parseWayfinder(xmlElement: Element): AST.WayfinderElement | null {
  const kind = "wayfinder";

  const categories = <AST.WayfinderCategoryElement[]>[];
  const xmlCategories = selectXMLChildren(xmlElement, "category");

  for (let i = 0, n = xmlCategories.length; i < n; ++i) {
    const xmlCategory = xmlCategories[i];
    const category = parseWayfinderCategory(xmlCategory);

    if (category) categories.push(category);
  }

  const attachedProperties = parseAttachedProperties(xmlElement);

  return { kind, categories, attachedProperties };
}

function parseWayfinderCategory(xmlElement: Element): AST.WayfinderCategoryElement | null {
  const kind = "category";

  const name = xmlElement.getAttribute("name");
  if (name === null) return null;

  const anchorId = xmlElement.getAttribute("anchor-id");
  if (anchorId === null) return null;

  const attachedProperties = parseAttachedProperties(xmlElement);

  return { kind, name, anchorId, attachedProperties };
}

function parseUIElement(xmlElement: Element): AST.UIElement | null {
  const width = parseMeasureSpec(xmlElement, "width");
  if (width === null) return null;

  const height = parseMeasureSpec(xmlElement, "height");
  if (height === null) return null;

  if (width === "preserve-aspect" && height === "preserve-aspect") return null;

  const aspectRatio = parseNumber(xmlElement, "aspect-ratio");
  if (!aspectRatio && (width === "preserve-aspect" || height === "preserve-aspect")) return null;

  const backgroundColor = parseColor(xmlElement, "background-color");
  const borderColor = parseColor(xmlElement, "border-color");

  const anchorId = xmlElement.getAttribute("anchor-id");

  const attachedProperties = parseAttachedProperties(xmlElement);

  const margin = parseMargin(xmlElement, "margin");
  const padding = parseMargin(xmlElement, "padding");
  const borderWidth = parseMargin(xmlElement, "border-width");
  const styleId = xmlElement.getAttribute("style-id");

  const base = {
    width,
    height,
    aspectRatio,
    backgroundColor,
    anchorId,
    attachedProperties,
    padding,
    margin,
    borderWidth,
    borderColor,
    styleId,
  };

  switch (xmlElement.nodeName) {
    case "linear-layout":
      return parseLinearLayout(xmlElement, base);
    case "collapsible-layout":
      return parseCollapsibleLayout(xmlElement, base);
    case "flexbox":
      return parseFlexbox(xmlElement, base);
    case "scrollview":
      return parseScrollView(xmlElement, base);
    case "image":
      return parseImage(xmlElement, base);
    case "item-atom":
      return parseItemAtom(xmlElement, base);
    case "badge":
      return parseBadge(xmlElement);
    default:
      return null;
  }
}

function parseStyle(xmlElement: Element): AST.StyleElement | null {
  const kind = "style";

  // TODO - in 1.0 this will be a child of a head/style element.
  // const xmlItemAtomStyle =
  // selectXMLChildren(xmlElement, 'item-atom-style')[0];

  const styles = <AST.StyleTypeElement[]>[];
  const xmlStyles = selectXMLChildren(xmlElement, "style");

  for (let i = 0, n = xmlStyles.length; i < n; ++i) {
    const xmlCategory = xmlStyles[i];
    const style = parseStyleType(xmlCategory);

    if (style) {
      styles.push(style);
    }
  }

  return {
    kind,
    styles,
  };
}

function parseBorder(xmlElement: Element): AST.BorderStyleElement | null {
  const kind = "border-style";

  const hasLeft = xmlElement.getAttribute("border-left-show");
  const hasTop = xmlElement.getAttribute("border-top-show");
  const hasRight = xmlElement.getAttribute("border-right-show");
  const hasBottom = xmlElement.getAttribute("border-bottom-show");

  if (!hasLeft && !hasTop && !hasRight && !hasBottom) {
    return null;
  }

  const color = xmlElement.getAttribute("border-color");
  const style = xmlElement.getAttribute("border-style");
  const width = xmlElement.getAttribute("border-width");

  if (!color && !style && !width) {
    return null;
  }

  const left = hasLeft ? { color, style, width } : null;
  const top = hasTop ? { color, style, width } : null;
  const right = hasRight ? { color, style, width } : null;
  const bottom = hasBottom ? { color, style, width } : null;

  return { kind, left, top, right, bottom };
}

function parseBackground(xmlElement: Element): AST.BackgroundStyleElement | null {
  const kind = "background-style";
  const color = parseColor(xmlElement, "background-color");
  const url = xmlElement.getAttribute("background-url");
  if (url === null && color === null) {
    return null;
  }

  return { kind, color, url };
}

function parseStyleType(xmlElement: Element): AST.StyleTypeElement | null {
  const id = xmlElement.getAttribute("id");
  if (id === null) {
    return null;
  }

  const paddingStyle = parsePaddingStyle(xmlElement);
  const marginStyle = parseMarginStyle(xmlElement);
  const backgroundStyle = parseBackground(xmlElement);
  const attachedProperties = parseAttachedProperties(xmlElement);
  const borderStyle = parseBorder(xmlElement);
  const textStyle = parseTextStyle(xmlElement);

  return { id, paddingStyle, marginStyle, backgroundStyle, attachedProperties, borderStyle, textStyle };
}

function parsePaddingStyle(xmlElement: Element): AST.PaddingStyleElement | null {
  const kind = "padding-style";
  const padding = parseMargin(xmlElement, "padding");
  return { kind, padding };
}

function parseMarginStyle(xmlElement: Element): AST.MarginStyleElement | null {
  const kind = "margin-style";
  const margin = parseMargin(xmlElement, "margin");
  return { kind, margin };
}

function parseTextStyle(xmlElement: Element): AST.TextStyleElement | null {
  const kind = "text-style";

  const fontFamily = parseFontFamily(xmlElement, "font-family");
  const fontWeight = parseFontWeight(xmlElement, "font-weight");
  const fontColor = parseColor(xmlElement, "font-color");
  const fontSize = parseNumber(xmlElement, "font-size");

  return {
    kind,
    fontFamily,
    fontWeight,
    fontColor,
    fontSize,
  };
}

function parseStorefront(xmlElement: Element): AST.StorefrontElement | null {
  const kind = "storefront";

  const version = "http://schemas.flipp.com/sfml/0.1";

  const title = xmlElement.getAttribute("title");
  const subtitle = xmlElement.getAttribute("subtitle");
  const uuid = xmlElement.getAttribute("uuid");
  const xmlHead = selectXMLChildren(xmlElement, "head")[0];
  if (!xmlHead || xmlHead.nodeName !== "head") return null;

  const xmlWayfinder = selectXMLChildren(xmlHead, "wayfinder")[0];

  const wayfinder = xmlWayfinder ? parseWayfinder(xmlWayfinder) : null;

  const xmlStyleHeader = selectXMLChildren(xmlHead, "styles")[0];
  const style = xmlStyleHeader ? parseStyle(xmlStyleHeader) : null;

  const xmlBody = selectXMLChildren(xmlElement, "body")[0];
  if (!xmlBody || xmlBody.nodeName !== "body") return null;

  const xmlContent = xmlBody.children[0];
  if (!xmlContent) return null;

  const content = parseUIElement(xmlContent);
  if (!content) return null;

  const attachedProperties = parseAttachedProperties(xmlElement);

  return {
    kind,
    version,
    title,
    subtitle,
    uuid,
    content,
    wayfinder,
    attachedProperties,
    style,
  };
}

export default parseStorefront;
export {
  parseItemAtom,
  parseImage,
  parseUIElement,
  parseWayfinder,
  parseUrlSource,
  parseText,
  parseStyle,
  parseFlexbox,
  parseLinearLayout,
};



// WEBPACK FOOTER //
// webpack://SFMLWeb/src/parse/sfml-semantic.ts